/**
 *
 * DESCRIPTION:
 * A helper class to load and instantiate Vue Components. It can be used in two ways either as a "typed-component" or an "untyped-component".
 *
 * A typed-component is instantiated by supplying a data-vue-component attribute with ONE (and only one) key to a vue-component from the window.vuecr.
 * This vue-component will then be loaded and instantiated as the root Vue app. This is used when the markup of the vue-component determines the markup to be rendered.
 *
 * An untyped-component is instantiated by NOT supplying a data-vue-component attribute. An untyped component is used
 * when you want to create an empty vue app that applies to some markup rendered from the backend. This markup can then utilize Vue syntax to render data,
 * as well as Vue component tags to instantiate any vue components supplied in the data-vue-component-dependencies attribute.
 *
 * ARGS:
 *  No specific args, but any arguments will be passed directly to the instantiated app.
 * */
import { createApp, reactive } from 'vue';
import VueMasonry from 'vue-masonry-css';

export default class VueComponentLoader {
    constructor(elem, args, callback) {
        this.appInstance = null;
        this.state = reactive(args); // make state reactive

        const vueComponent = elem.getAttribute('data-vue-component');
        const state = this.state;
        if (vueComponent) {
            window.vuecr.loadComponentClass(vueComponent)
                .then((VueComponent) => {
                    VueComponent.props = ['state']; // Define the prop
                    this.appInstance = createApp(VueComponent, { state }); // Pass the prop when creating the app
                    this.appInstance.use(VueMasonry);
                    this.appInstance.mount(elem);
                    if (callback) callback();
                });
        } else {
            const createUntypedApp = (deps = {}) => {
                this.appInstance = createApp({
                    data() { return { state: this.state }; },
                    components: deps,
                });
                this.appInstance.mount(elem);
            };

            const depsAttr = elem.getAttribute('data-vue-component-dependencies');
            const componentDependencies = depsAttr ? depsAttr.split(',').map((cd) => cd.trim()) : [];

            if (componentDependencies.length > 0) {
                window.vuecr.loadComponentClasses(componentDependencies)
                    .then((result) => {
                        const deps = {};
                        componentDependencies.forEach((cd, i) => {
                            deps[cd.replace('-', '')] = result[i];
                        });
                        createUntypedApp(deps);
                    });
            } else {
                createUntypedApp();
            }
        }

        this._maskedHelpers = {
            getAppInstance: () => this.appInstance,
            setData: (data) => {
                for (const key in data) {
                    this.state[key] = data[key]; // Vue automatically triggers re-render
                }
            },
        };
    }

    getAppInstance() {
        return this._maskedHelpers.getAppInstance();
    }

    setData(data) {
        this._maskedHelpers.setData(data);
    }
}
